import { Suspense, useCallback, useEffect, useRef } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Warning } from "@phosphor-icons/react"
import { CustomSelect } from "@/components/ui/custom-select.tsx"
import { DrawerTitle } from "@/components/ui/drawer.tsx"
import { FormField } from "@/components/ui/form.tsx"
import { TextArea } from "@/components/ui/input"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { textAreaDefaultMaxLength } from "@/models/common"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { CareProviderValue } from "@/modules/care-provider/simple-search/care-provider-value"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code.tsx"
import { BetterDocManualSearchBanner } from "@/modules/patients/detail/better-doc/components/better-doc-manual-search-banner"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { MedicalHistory } from "@/modules/patients/detail/medical-history.tsx"
import { ReferralLetterCopilotButton } from "@/modules/patients/detail/referral/components/referral-letter-copilot-button"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { DOCUMENT_LANGUAGES } from "@/modules/patients/hooks/useDefaultDocumentLanguage.ts"
import { useEpisodeOfCareByProgressNoteId } from "@/modules/patients/hooks/useEpisodeOfCareByProgressNoteId"
import { usePatientEpisodesOfCareData } from "@/modules/patients/hooks/usePatientEpisodesOfCareData"
import { useProgressNoteData } from "@/modules/patients/hooks/useProgressNoteData"

type ReferralFormProps = {
  fetchKey?: number
  progressNoteId: string
}

const ReferralForm = (props: ReferralFormProps) => {
  const { fetchKey, progressNoteId } = props
  const { t } = useTranslation()
  const isEditMode = useEditMode()
  const careProvider = useReferralStoreContext((state) => state.careProvider)
  const timeToTreat = useReferralStoreContext((state) => state.timeToTreat)

  const patientEpisodesOfCareData = usePatientEpisodesOfCareData()
  const { progressNoteAndParentData } = useProgressNoteData(patientEpisodesOfCareData, isEditMode)
  const { isBetterDocUsed } = useEpisodeOfCareByProgressNoteId(progressNoteId)
  const copilotButtonRef = useRef<HTMLButtonElement>(null)

  const form = useFormContext()
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = form
  const documentLanguage = useWatch({ control, name: "documentLanguage" })

  useEffect(() => {
    if (getValues("reasoning")) {
      return
    }

    if (copilotButtonRef.current) {
      const selectedProgressNote = progressNoteAndParentData.find(
        (note) => note.episodeOfCareProgressNoteData.id === progressNoteId,
      )?.episodeOfCareProgressNoteData

      if (selectedProgressNote && (selectedProgressNote.episodeOfCareId !== null || selectedProgressNote.anamnesis)) {
        copilotButtonRef.current.click()
      }
    }
  }, [getValues, progressNoteAndParentData, progressNoteId])

  const handleOnReferralLetterCopilotComplete = useCallback(
    (data: {
      text: string
      fileHash: string
      aiRequestDurationMs: number
      aiAnonymizationRequestDurationMs: number
      aiFeatureId: string
    }) => {
      setValue("reasoning", data.text, { shouldDirty: true })

      FaroInsightsWrapper.pushEvent("aifeature_referral_letter_copilot", {
        aiFeatureId: data.aiFeatureId,
        feature: "Referral Letter Copilot",
        fileHash: data.fileHash,
        aiRequestDurationMs: data.aiRequestDurationMs.toString(),
        aiAnonymizationRequestDurationMs: data.aiAnonymizationRequestDurationMs.toString(),
        sentAt: new Date().toISOString(),
      })
    },
    [setValue],
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="pb-5">
        <FormField
          control={control}
          name="documentLanguage"
          render={({ field: { value } }: { field: { value: string } }) => {
            return (
              <CustomSelect
                testId="document-language-select"
                onSelect={(val: string) => setValue("documentLanguage", val)}
                value={value || "GERMAN"}
                label={`${t("patient.referralLetter.documentLanguage")}`}
                options={DOCUMENT_LANGUAGES}
                showValueLabel
                useTranslatedValue
                className="w-full"
              />
            )
          }}
        />
      </div>
      <div className="pb-5">
        <DrawerTitle className="text-md animate-fade-in-bottom text-on-surface-subdued pb-4 font-medium">
          {t("common.to")}
        </DrawerTitle>
        {careProvider ? (
          <CareProviderValue value={careProvider} viewOnly />
        ) : isBetterDocUsed ? (
          <BetterDocManualSearchBanner />
        ) : (
          <div className="bg-critical-surface text-on-critical-surface flex h-12 cursor-pointer items-center gap-4 rounded-lg px-4 text-sm font-normal">
            <Warning weight="fill" className="text-on-critical-surface" />
            <span className="truncate">{t("patient.referralLetter.careProviderWarning")}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 self-start">
        <div className="text-on-surface leading-6 font-medium">{t("patient.referralLetter.reason")}</div>
        <ReferralLetterCopilotButton
          ref={copilotButtonRef}
          language={documentLanguage}
          progressNoteId={progressNoteId}
          progressNoteAndParentData={progressNoteAndParentData}
          onComplete={handleOnReferralLetterCopilotComplete}
        />
      </div>
      <Controller
        control={control}
        name="diagnosisCode"
        render={({ field: { name, onChange, value }, fieldState: { invalid } }) => (
          <Suspense fallback={null}>
            <DiagnosisCode
              defaultValue={value}
              name={name}
              errors={errors}
              invalid={invalid}
              value={value}
              fetchKey={fetchKey}
              onSelect={onChange}
            />
          </Suspense>
        )}
      />
      <Controller
        name="reasoning"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextArea
            label={t("patient.referralLetter.reasoning")}
            rows={3}
            textLimit={textAreaDefaultMaxLength}
            error={error?.message}
            translateError
            {...field}
          />
        )}
      />
      <div className="grid gap-0.5">
        {timeToTreat && (
          <>
            <span className="text-on-surface-subdued truncate text-xs leading-4 tracking-[.25px]">
              {t("patient.referralLetter.timeToTreat")}
            </span>
            <div className="text-on-surface text-sm leading-5">
              {t(`episodeOfCare.progressNote.pointOfCare.timeToTreatOptionList.${timeToTreat.toLowerCase()}`)}
            </div>
          </>
        )}
      </div>

      <div className="grid gap-4">
        <MedicalHistory />
      </div>

      <div className="grid gap-4">
        <div className="text-on-surface leading-6 font-medium">{t("patient.referralLetter.sendCopyTo")}</div>
        <Controller
          name="careProviderCopyToIds"
          control={control}
          render={({ field: { name, value } }) => (
            <CareProviderSearch
              label={t("episodeOfCare.progressNote.pointOfCare.careProvider")}
              searchType="NONE"
              name={name}
              value={value}
              analyticsSectionName="documents_referral"
              onSelect={(val) => {
                const cpValues = getValues("careProviderCopyToIds") || []
                cpValues.push(val)
                setValue("careProviderCopyToIds", cpValues, { shouldDirty: true })
              }}
              onRemove={(selected) => {
                const cpValues = getValues("careProviderCopyToIds")
                setValue(
                  "careProviderCopyToIds",
                  cpValues.filter((value: string) => value !== selected),
                  { shouldDirty: true, shouldValidate: true },
                )
              }}
              isMultiple
              maxItems={3}
              error={undefined}
            />
          )}
        />
      </div>
    </div>
  )
}

export default ReferralForm
